import React, { useState, useEffect, useMemo } from "react";
import apiClient from "../../api/apiClient";
import { useToast } from "../../context/toastContext";
import { useEscapeKey } from "../ui/useEscapeKey";
import { formatWeightFromGrams, toGrams } from "../../utils/weightUtils";
import ColorDot from "../ColorDot/ColorDot";
import "./CreateMixModal.css";

const SIZE_PRESETS = [0.5, 0.75, 1, 1.5, 2];
const NON_COMPONENT_TYPES = new Set(["mix-flower", "mix-preroll"]);

export default function CreateMixModal({ licenseNo, locationId, onClose, onSuccess }) {
    const toast = useToast();
    useEscapeKey(onClose);

    const [mixType, setMixType] = useState("mix-flower");
    const [productName, setProductName] = useState("");
    const [quantity, setQuantity] = useState("");
    const [unitSize, setUnitSize] = useState(1);
    const [customSize, setCustomSize] = useState("");
    const [useCustom, setUseCustom] = useState(false);
    const [price, setPrice] = useState("");
    const [sku, setSku] = useState("");
    const [description, setDescription] = useState("");
    const [deductInventory, setDeductInventory] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const [availableProducts, setAvailableProducts] = useState([]);
    const [search, setSearch] = useState("");
    const [selectedComponents, setSelectedComponents] = useState([]);

    useEffect(() => {
        if (!licenseNo) return;
        const locationParam = locationId ? `&locationId=${locationId}` : "";
        apiClient.get(`/products?licenseNo=${licenseNo}${locationParam}`)
            .then((res) => {
                const data = Array.isArray(res.data) ? res.data : [];
                setAvailableProducts(data.filter((p) => {
                    const pt = (p.product_type || "").toLowerCase();
                    const unit = (p.weight_unit || "g").toLowerCase();
                    // Any weight-based product can be blended into a mix; only
                    // unit-counted products and existing mixes (no nesting) are excluded.
                    return !NON_COMPONENT_TYPES.has(pt) && p.quantity > 0 && unit !== "units";
                }));
            })
            .catch(() => setAvailableProducts([]));
    }, [licenseNo, locationId]);

    const filteredProducts = useMemo(() => {
        const selectedIds = new Set(selectedComponents.map((c) => c.productId));
        return availableProducts.filter(
            (p) => !selectedIds.has(p.id) &&
                (p.product_name || "").toLowerCase().includes(search.toLowerCase())
        );
    }, [availableProducts, selectedComponents, search]);

    const addComponent = (product) => {
        setSelectedComponents((prev) => [
            ...prev,
            {
                productId: product.id,
                productName: product.product_name,
                color: (product.color || "").toLowerCase(),
                availableGrams: toGrams(parseFloat(product.quantity) || 0, product.weight_unit || "g"),
                ratioPct: "",
                gramsToDeduct: "",
            },
        ]);
        setSearch("");
    };

    const removeComponent = (productId) => {
        setSelectedComponents((prev) => prev.filter((c) => c.productId !== productId));
    };

    const updateComponent = (productId, field, value) => {
        setSelectedComponents((prev) =>
            prev.map((c) => c.productId === productId ? { ...c, [field]: value } : c)
        );
    };

    const effectiveSize = useCustom ? parseFloat(customSize) || 0 : unitSize;

    const hasAnyRatio = selectedComponents.some((c) => c.ratioPct !== "");
    const ratioSum = selectedComponents.reduce((sum, c) => sum + (parseFloat(c.ratioPct) || 0), 0);
    const ratioValid = !hasAnyRatio || (
        selectedComponents.every((c) => c.ratioPct !== "") &&
        Math.abs(ratioSum - 100) < 0.01
    );

    const deductionValid = !deductInventory || selectedComponents.every((c) => {
        const g = parseFloat(c.gramsToDeduct);
        return g > 0 && g <= c.availableGrams;
    });

    const canSubmit = useMemo(() => {
        return (
            selectedComponents.length >= 2 &&
            productName.trim().length > 0 &&
            parseFloat(quantity) > 0 &&
            (mixType !== "mix-preroll" || effectiveSize > 0) &&
            ratioValid &&
            deductionValid &&
            !submitting
        );
    }, [selectedComponents, productName, quantity, mixType, effectiveSize, ratioValid, deductionValid, submitting]);

    const handleSubmit = async () => {
        if (!canSubmit) return;
        setSubmitting(true);
        try {
            const components = selectedComponents.map((c) => ({
                productId: c.productId,
                ratioPct: c.ratioPct !== "" ? parseFloat(c.ratioPct) : null,
                gramsToDeduct: deductInventory ? parseFloat(c.gramsToDeduct) : undefined,
            }));
            await apiClient.post("/mixes", {
                licenseNo,
                locationId: locationId || null,
                productName: productName.trim(),
                mixType,
                components,
                quantity: parseFloat(quantity),
                unitSize: mixType === "mix-preroll" ? effectiveSize : undefined,
                price: price ? parseFloat(price) : null,
                sku: sku.trim() || null,
                description: description.trim() || null,
                deductInventory,
            });
            toast.success(`Created mix "${productName.trim()}" with ${selectedComponents.length} components`);
            onSuccess?.();
            onClose();
        } catch (err) {
            const msg = err.response?.data?.error || "Failed to create mix";
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="cpm-backdrop" onClick={onClose} role="dialog" aria-modal="true">
            <div className="cmm" onClick={(e) => e.stopPropagation()}>
                <div className="cmm__header">
                    <h3 className="cmm__title">Create Mix</h3>
                    <button className="cpm__close" onClick={onClose} aria-label="Close">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M2 2L14 14M14 2L2 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                    </button>
                </div>

                <div className="cmm__body">
                    {/* Mix type toggle */}
                    <div className="cmm__type-toggle">
                        <button
                            className={`cmm__type-btn${mixType === "mix-flower" ? " cmm__type-btn--active" : ""}`}
                            onClick={() => setMixType("mix-flower")}
                        >
                            Mixed Flower
                        </button>
                        <button
                            className={`cmm__type-btn${mixType === "mix-preroll" ? " cmm__type-btn--active" : ""}`}
                            onClick={() => setMixType("mix-preroll")}
                        >
                            Mixed Pre-Roll
                        </button>
                    </div>

                    {/* Product name */}
                    <div className="prm__field">
                        <label className="prm__label">Product Name</label>
                        <input
                            type="text"
                            className="prm__input"
                            placeholder="e.g. House Blend, Rainbow Mix"
                            value={productName}
                            onChange={(e) => setProductName(e.target.value)}
                        />
                    </div>

                    {/* Component picker */}
                    <div className="prm__field">
                        <label className="prm__label">Select Products ({selectedComponents.length} selected)</label>
                        <input
                            type="text"
                            className="prm__input"
                            placeholder="Search products..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        {search && filteredProducts.length > 0 && (
                            <div className="cmm__search-results">
                                {filteredProducts.slice(0, 8).map((p) => (
                                    <div
                                        key={p.id}
                                        className="cmm__search-item"
                                        onClick={() => addComponent(p)}
                                    >
                                        <ColorDot color={(p.color || "").toLowerCase()} className="cmm__search-dot" />
                                        <span className="cmm__search-name">{p.product_name}</span>
                                        <span className="cmm__search-qty">{formatWeightFromGrams(toGrams(parseFloat(p.quantity) || 0, p.weight_unit || "g"))}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Selected components */}
                    {selectedComponents.length > 0 && (
                        <div className="cmm__components">
                            {selectedComponents.map((comp) => (
                                <div key={comp.productId} className="cmm__comp">
                                    <div className="cmm__comp-header">
                                        <ColorDot color={comp.color} className="cmm__comp-dot" />
                                        <span className="cmm__comp-name">{comp.productName}</span>
                                        <span className="cmm__comp-avail">{formatWeightFromGrams(comp.availableGrams)}</span>
                                        <button className="cmm__comp-remove" onClick={() => removeComponent(comp.productId)}>
                                            <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                                                <path d="M2 2L14 14M14 2L2 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                            </svg>
                                        </button>
                                    </div>
                                    <div className="cmm__comp-fields">
                                        <div className="cmm__comp-field">
                                            <label className="cmm__comp-label">Ratio %</label>
                                            <input
                                                type="number"
                                                className="cmm__comp-input"
                                                placeholder="Optional"
                                                value={comp.ratioPct}
                                                onChange={(e) => updateComponent(comp.productId, "ratioPct", e.target.value)}
                                                min="0"
                                                max="100"
                                                step="0.01"
                                            />
                                        </div>
                                        {deductInventory && (
                                            <div className="cmm__comp-field">
                                                <label className="cmm__comp-label">Grams to deduct</label>
                                                <input
                                                    type="number"
                                                    className="cmm__comp-input"
                                                    placeholder="0"
                                                    value={comp.gramsToDeduct}
                                                    onChange={(e) => updateComponent(comp.productId, "gramsToDeduct", e.target.value)}
                                                    min="0"
                                                    max={comp.availableGrams}
                                                    step="0.01"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                            {hasAnyRatio && (
                                <span className={`cmm__ratio-sum${ratioValid ? "" : " cmm__ratio-sum--invalid"}`}>
                                    Total: {ratioSum.toFixed(1)}%{!ratioValid && " (must equal 100%)"}
                                </span>
                            )}
                        </div>
                    )}

                    {/* Quantity and unit size */}
                    <div className="prm__row">
                        <div className="prm__field prm__field--half">
                            <label className="prm__label">
                                {mixType === "mix-preroll" ? "Number of Pre-Rolls" : "Total Weight (grams)"}
                            </label>
                            <input
                                type="number"
                                className="prm__input"
                                value={quantity}
                                onChange={(e) => setQuantity(e.target.value)}
                                min="0"
                                step={mixType === "mix-preroll" ? "1" : "0.01"}
                            />
                        </div>
                        {mixType === "mix-preroll" && (
                            <div className="prm__field prm__field--half">
                                <label className="prm__label">Unit Size (g/pre-roll)</label>
                                <div className="prm__size-presets">
                                    {SIZE_PRESETS.map((s) => (
                                        <button
                                            key={s}
                                            className={`prm__size-btn${!useCustom && unitSize === s ? " prm__size-btn--active" : ""}`}
                                            onClick={() => { setUseCustom(false); setUnitSize(s); }}
                                        >
                                            {s}g
                                        </button>
                                    ))}
                                    <button
                                        className={`prm__size-btn${useCustom ? " prm__size-btn--active" : ""}`}
                                        onClick={() => setUseCustom(true)}
                                    >
                                        Custom
                                    </button>
                                </div>
                                {useCustom && (
                                    <input
                                        type="number"
                                        className="prm__input"
                                        placeholder="Grams"
                                        value={customSize}
                                        onChange={(e) => setCustomSize(e.target.value)}
                                        min="0.1"
                                        step="0.1"
                                    />
                                )}
                            </div>
                        )}
                    </div>

                    {/* Price and SKU */}
                    <div className="prm__row">
                        <div className="prm__field prm__field--half">
                            <label className="prm__label">Price</label>
                            <input
                                type="number"
                                className="prm__input"
                                placeholder="Optional"
                                value={price}
                                onChange={(e) => setPrice(e.target.value)}
                                min="0"
                                step="0.01"
                            />
                        </div>
                        <div className="prm__field prm__field--half">
                            <label className="prm__label">SKU</label>
                            <input
                                type="text"
                                className="prm__input"
                                placeholder="Optional"
                                value={sku}
                                onChange={(e) => setSku(e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Description */}
                    <div className="prm__field">
                        <label className="prm__label">Description</label>
                        <textarea
                            className="prm__textarea"
                            placeholder="Optional"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={2}
                        />
                    </div>

                    {/* Deduct inventory toggle */}
                    <label className="cmm__deduct-toggle">
                        <input
                            type="checkbox"
                            checked={deductInventory}
                            onChange={(e) => setDeductInventory(e.target.checked)}
                        />
                        <span>Deduct from source product inventory</span>
                    </label>
                </div>

                <div className="prm__footer">
                    <button className="prm__btn prm__btn--cancel" onClick={onClose}>Cancel</button>
                    <button
                        className="prm__btn prm__btn--submit"
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                    >
                        {submitting ? "Creating..." : "Create Mix"}
                    </button>
                </div>
            </div>
        </div>
    );
}
